import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useMutation } from "react-query";
import * as apiClient from "../api-client";
import { useAppContext } from "../contexts/AppContext";
import { FaLock } from 'react-icons/fa';
import { useEffect } from "react";

export type ResetPasswordFormData = {
  password: string;
};

const ResetPassword = () => {
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { showToast } = useAppContext();
  const { register, handleSubmit, formState: { errors }, reset } = useForm<ResetPasswordFormData>();
  const { resetToken } = useParams<{ resetToken?: string }>();

  const mutation = useMutation((data: ResetPasswordFormData) => {
    if (!resetToken) {
      throw new Error("Reset token is missing");
    }
    return apiClient.resetPassword(resetToken, data.password);
  }, {
    onSuccess: () => {
      showToast({ message: "Password updated successfully!", type: "SUCCESS" });
      reset();
    },
    onError: (error: Error) => {
      showToast({ message: error.message, type: "ERROR" });
    },
  });

  const onSubmit = handleSubmit((data) => {
    mutation.mutate(data);
  });

  return (
    <div className="mt-20 flex items-center justify-center">
      <form
        className="bg-white p-6 md:p-10 rounded-lg shadow-md w-full max-w-sm md:max-w-md border border-light"
        onSubmit={onSubmit}
      >
        <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">Reset Password</h2>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2">
            New Password
            <div className="flex items-center border rounded w-full py-2 px-3 mt-1 text-gray-700 leading-tight focus:outline-none focus:shadow-outline">
              <FaLock className="mr-3 text-halo-pink" />
              <input
                type="password"
                className="flex-1 focus:outline-none"
                {...register("password", { 
                  required: "This field is required", 
                  minLength: { 
                    value: 6, 
                    message: "Password must be at least 6 characters long" 
                  } 
                })}
              />
            </div>
            {errors.password && (
              <p className="text-red-500 text-xs italic">{errors.password.message}</p>
            )}
          </label>
        </div>
        <div className="flex items-center justify-center">
          <button
            type="submit"
            className="bg-halo-pink text-white py-2 px-6 rounded hover:bg-pink-800 focus:outline-none focus:shadow-outline flex items-center justify-center relative"
            aria-label="Update Password"
            disabled={mutation.isLoading}
          >
            {mutation.isLoading ? (
              <svg
                className="animate-spin h-5 w-5 text-white m-auto"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            ) : (
              "Update"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResetPassword;